<script setup lang="ts">
import { ref, type PropType } from 'vue'
import GbButton from '@/components/GbButton.vue'
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel
} from 'reka-ui'

interface DropdownItem {
  label: string
  subtitle?: string
  icon?: string
  disabled?: boolean
  command?: () => void
}

defineProps({
  dropdownId: {
    type: String,
    required: true
  },
  buttonTheme: {
    type: String,
    default: 'bluePrimary',
    required: false
  },
  buttonAriaLabel: {
    type: String,
    required: true
  },
  buttonSize: {
    type: String,
    required: false,
    default: 'md'
  },
  additionalClasses: {
    type: String,
    required: false,
    default: ''
  },
  menuContentClasses: {
    type: String,
    required: false,
    default: ''
  },
  items: {
    type: Array as PropType<Array<DropdownItem>>,
    default: () => []
  },
  menuLabel: {
    type: String,
    required: false
  }
})

const isOpen = ref(false)
const emit = defineEmits(['dropdown:opened'])

const handleOpenChange = (open: boolean) => {
  isOpen.value = open
  if (open) {
    emit('dropdown:opened')
  }
}
</script>

<template>
  <DropdownMenuRoot :open="isOpen" @update:open="handleOpenChange">
    <DropdownMenuTrigger as-child>
      <GbButton
        v-bind="$attrs"
        :gbTheme="buttonTheme"
        :aria-label="buttonAriaLabel"
        aria-haspopup="true"
        :aria-controls="dropdownId"
        :size="buttonSize"
        :class="additionalClasses"
      >
        <slot name="buttonContents">
          Dropdown Button
          <span class="material-icons-round text-display-sm">arrow_drop_down</span>
        </slot>
      </GbButton>
    </DropdownMenuTrigger>
    <DropdownMenuContent :id="dropdownId" align="start" :class="['min-w-[--reka-dropdown-menu-trigger-width] max-w-md overflow-hidden rounded-md border border-gray-200 shadow-md bg-white py-2 z-20', menuContentClasses]">
      <DropdownMenuLabel v-if="menuLabel" class="px-3 py-2 text-sm font-bold text-gray-700 uppercase tracking-wider">
        {{ menuLabel }}
      </DropdownMenuLabel>
      <template v-for="(item, index) in items" :key="index">
        <DropdownMenuItem
          :disabled="item.disabled"
          :class="[
            'relative flex items-center px-3 outline-none',
            item.icon || item.subtitle ? 'gap-3 py-2.5' : 'py-2',
            item.disabled 
              ? 'opacity-40 cursor-not-allowed' 
              : 'cursor-pointer hover:bg-gray-300 focus:bg-gray-300'
          ]"
          @select="!item.disabled && item.command && item.command()"
        >
          <span v-if="item.icon" :class="['material-icons-outlined text-lg', item.disabled ? 'text-gray-400' : 'text-gray-700']">
            {{ item.icon }}
          </span>
          <div v-if="item.subtitle" class="flex-1">
            <div :class="['font-semibold text-md', item.disabled ? 'text-gray-800' : 'text-gray-900']">{{ item.label }}</div>
            <div class="text-sm text-gray-700">{{ item.subtitle }}</div>
          </div>
          <span v-else class="text-md">{{ item.label }}</span>
        </DropdownMenuItem>
      </template>
    </DropdownMenuContent>
  </DropdownMenuRoot>
</template>
